import PageHeading from "../../../components/desktop/PageHeading";
import React, { useState, useEffect, useRef, useMemo } from "react";

import { toast } from "react-toastify";

import "./Holidaymaster.css";
import back from "../../../assets/back.png";
import { FaRegEdit } from "react-icons/fa";
import { MdDeleteOutline } from "react-icons/md";
import { useAuthStore } from "../../../store/authStore";
import { useLoaderStore } from "../../../store/loaderStore";
import { api } from "../../../utils/api";
import Loader from "../../common/Loader";
import { FiCalendar, FiChevronDown, FiX } from "react-icons/fi";

// ── Today's date as "YYYY-MM-DD", used so the Holiday Date picker only
// allows present and future dates (never a past date). ──
const getTodayDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getHolidayLocationIds = (holiday) => {
  let value = holiday.location ?? holiday.location_id ?? [];
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      value = value.split(",");
    }
  }
  const ids = (Array.isArray(value) ? value : [value])
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0);
  return [...new Set(ids)].map(String);
};

export default function Holidaymaster() {
  const { showLoader, hideLoader } = useLoaderStore();

  const [holidays, setHolidays] = useState([]);

  const { user, isAuthenticated } = useAuthStore();

  const companyId = user?.company_id;

  const todayDate = getTodayDateString();

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);

  const [locations, setLocations] = useState([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const locationDropdownRef = useRef(null);
  const [locationFilter, setLocationFilter] = useState("");
  const [appliedLocation, setAppliedLocation] = useState("");

  const [form, setForm] = useState({
    holiday_name: "",
    holiday_date: "",
    holiday_type: "",
    location: [],
    description: "",
    status: true,
  });

  useEffect(() => {
    if (!showForm) {
      setShowLocationDropdown(false);
      return;
    }
    const handleClickOutside = (event) => {
      if (!locationDropdownRef.current?.contains(event.target)) {
        setShowLocationDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showForm]);

  const toggleLocation = (id) => {
    setForm((previous) => ({
      ...previous,
      location: previous.location.includes(id)
        ? previous.location.filter((locationId) => locationId !== id)
        : [...previous.location, id],
    }));
  };

  const selectedLocations = form.location.map((id) => ({
    id,
    location_name: locations.find((location) => String(location.id) === id)?.location_name || `Location #${id}`,
  }));
  const locationOptions = [
    ...locations,
    ...selectedLocations.filter((selected) => !locations.some((location) => String(location.id) === selected.id)),
  ];
  const allLocationIds = locationOptions.map((location) => String(location.id));
  const allLocationsSelected = allLocationIds.length > 0 &&
    allLocationIds.every((id) => form.location.includes(id));
  const someLocationsSelected = allLocationIds.some((id) => form.location.includes(id));

  const fetchHolidays = async (showToast = false) => {
    try {
      showLoader("master");
      const response = await api.get(`/holiday/list/${companyId}`);

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to load holidays");
      }

      setHolidays(result);

      if (showToast) {
        toast.success("Holidays fetched successfully");
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to load holidays");
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (companyId && isAuthenticated) {
      fetchHolidays(true);
    }
  }, [companyId, isAuthenticated]);

  // Locations feed both the list's location filter and the form's picker.
  useEffect(() => {
    if (!companyId || !isAuthenticated) return;

    const fetchLocations = async () => {
      try {
        const response = await api.get(`/location/active-list/${companyId}`);

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || "Failed to load locations");
        }

        setLocations(
          Array.isArray(result)
            ? result
            : Array.isArray(result.data)
              ? result.data
              : [],
        );
      } catch (error) {
        console.error(error);
        toast.error(error.message || "Failed to load locations");
      }
    };

    fetchLocations();
  }, [showForm, companyId, isAuthenticated]);

  // ── Holidays for the applied location filter ──
  const filteredHolidays = useMemo(() => {
    const list = Array.isArray(holidays) ? holidays : [];

    if (!appliedLocation) return list;

    return list.filter((item) =>
      getHolidayLocationIds(item).includes(appliedLocation),
    );
  }, [holidays, appliedLocation]);

  const toggleStatus = async (item) => {
    try {
      showLoader("master");
      const response = await api.put(`/holiday/update/${item.id}`, {
        holiday_name: item.holiday_name,
        holiday_date: item.holiday_date,
        holiday_type: item.holiday_type,
        location: getHolidayLocationIds(item).map(Number),
        description: item.description,
        isactive: item.isactive === 1 ? 0 : 1,
      });

      const result = await response.json();

      if (!response.ok || result?.status === false) {
        throw new Error(result.message || "Failed to update status");
      }

      await fetchHolidays();

      toast.success(
        `Holiday ${
          item.isactive === 1 ? "deactivated" : "activated"
        } successfully`,
      );
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to update status");
    } finally {
      hideLoader();
    }
  };

  const handleEdit = async (item) => {
    try {
      showLoader("master");
      const response = await api.get(`/holiday/${item.id}`);

      const result = await response.json();
      if (!response.ok || result?.status === false) {
        throw new Error(result.message || "Failed to load holiday");
      }
      const data = result.data ?? result;

      setEditId(item.id);

      setForm({
        holiday_name: data.holiday_name || "",
        holiday_date: data.holiday_date || "",
        holiday_type: data.holiday_type || "",
        location: getHolidayLocationIds(data),
        description: data.description || "",
        status: data.isactive === 1,
      });

      setShowForm(true);
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to load holiday");
    } finally {
      hideLoader();
    }
  };

  const validateDateYear = (date, fieldName) => {
    if (!date) {
      toast.warning(`Please select ${fieldName}`);
      return false;
    }

    const year = date.split("-")[0];

    if (!/^\d{4}$/.test(year)) {
      toast.warning(`${fieldName} must have a 4-digit year`);
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!form.holiday_name) {
      toast.warning("Holiday Name is required");
      return;
    }

    if (!validateDateYear(form.holiday_date, "Holiday Date")) {
      return;
    }

    if (form.holiday_date < todayDate) {
      toast.warning("Holiday Date cannot be in the past");
      return;
    }

    if (!form.holiday_type) {
      toast.warning("Holiday Type is required");
      return;
    }

    if (!form.location.length) {
      toast.warning("Location is required");
      return;
    }

    try {
      showLoader("master");
      let response;

      const addPayload = {
        company_id: companyId,
        holiday_name: form.holiday_name,
        holiday_date: form.holiday_date,
        holiday_type: form.holiday_type,
        location: form.location.map(Number),
        description: form.description,
      };

      const updatePayload = {
        holiday_name: form.holiday_name,
        holiday_date: form.holiday_date,
        holiday_type: form.holiday_type,
        location: form.location.map(Number),
        description: form.description,
        isactive: form.status ? 1 : 0,
      };

      if (editId) {
        response = await api.put(`/holiday/update/${editId}`, updatePayload);
      } else {
        response = await api.post(`/holiday/add`, addPayload);
      }

      const result = await response.json();

      if (!response.ok || result?.status === false) {
        throw new Error(result.message || "Operation failed");
      }

      await fetchHolidays();
      toast.success(
        editId ? "Holiday updated successfully" : "Holiday added successfully",
      );

      setForm({
        holiday_name: "",
        holiday_date: "",
        holiday_type: "",
        location: [],
        description: "",
        status: true,
      });

      setEditId(null);
      setShowForm(false);
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Operation failed");
    } finally {
      hideLoader();
    }
  };

  const handleCancel = () => {
    setForm({
      holiday_name: "",
      holiday_date: "",
      holiday_type: "",
      location: [],
      description: "",
      status: true,
    });

    setEditId(null);
    setShowForm(false);
  };

  const handleDelete = async (holidayId) => {
    try {
      showLoader();
      const response = await api.delete(`/holiday/delete/${holidayId}`);

      if (!response.ok) {
        throw new Error("Delete failed");
      }

      await fetchHolidays();

      toast.success("Holiday deleted successfully");
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Delete failed");
    } finally {
      hideLoader();
    }
  };

  const formatDate = (date) => {
    if (!date) return "";

    const [year, month, day] = date.split("T")[0].split("-");

    return `${day}-${month}-${year}`;
  };

  return (
    <div className="holiday-master-container">
      <PageHeading>Holiday Master</PageHeading>
      {!showForm ? (
        <>
          <div className="holiday-master-top">
            <div className="holiday-filter-group">
              <select
                className="holiday-location-filter"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                aria-label="Filter by location"
              >
                <option value="">All Locations</option>
                {locations.map((location) => (
                  <option key={location.id} value={String(location.id)}>
                    {location.location_name}
                  </option>
                ))}
              </select>

              <button
                type="button"
                className="holiday-search-btn"
                onClick={() => setAppliedLocation(locationFilter)}
              >
                Search
              </button>
            </div>

            <button
              className="add-holiday-btn"
              onClick={() => {
                setEditId(null);

                setForm({
                  holiday_name: "",
                  holiday_date: "",
                  holiday_type: "",
                  location: [],
                  description: "",
                  status: true,
                });

                setShowForm(true);
              }}
            >
              + Add Holiday
            </button>
          </div>

          <div className="holiday-table-wrapper">
            <table className="holiday-table">
              <thead>
                <tr>
                  <th className="serial-col">Sr.no</th>
                  <th>Holiday Name</th>
                  <th>Holiday Date</th>
                  <th>Holiday Type</th>
                  <th className="status-col">Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredHolidays.length === 0 && (
                  <tr>
                    <td colSpan="6" className="holiday-no-data">
                      No holidays found.
                    </td>
                  </tr>
                )}

                {filteredHolidays.map((item, index) => (
                  <tr key={item.id}>
                    <td className="serial-col">{index + 1}</td>

                    <td>{item.holiday_name}</td>

                    <td>{formatDate(item.holiday_date)}</td>

                    <td>{item.holiday_type}</td>

                    <td className="Hstatus-col">
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={item.isactive === 1}
                          onChange={() => toggleStatus(item)}
                        />
                        <span className="slider"></span>
                      </label>
                    </td>

                    <td className="action-cell">
                      <FaRegEdit
                        className="edit-icon"
                        onClick={() => handleEdit(item)}
                      />

                      {/* <MdDeleteOutline
                                                className="delete-icon"
                                                onClick={() => handleDelete(item.id)}
                                            /> */}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="holiday-form-container">
          <div className="holiday-form-header">
            <img
              src={back}
              alt="Back"
              className="back-btn-img"
              onClick={handleCancel}
            />

            <h3>{editId ? "Edit Holiday" : "Add Holiday"}</h3>
          </div>

          <div className="holiday-form">
            <div className="form-group">
              <label>
                Holiday Name <span>*</span>
              </label>

              <input
                type="text"
                value={form.holiday_name}
                onChange={(e) =>
                  setForm({
                    ...form,
                    holiday_name: e.target.value,
                  })
                }
                placeholder="Enter Holiday Name"
              />
            </div>

            <div className="form-group">
              <label>
                Holiday Date <span>*</span>
              </label>

              <div className="holiday-date-wrapper">
                <input
                  type="text"
                  className="holiday-date-display"
                  value={
                    form.holiday_date
                      ? form.holiday_date.split("-").reverse().join("-")
                      : ""
                  }
                  placeholder="dd-mm-yyyy"
                  readOnly
                />

                <FiCalendar className="holiday-calendar-icon" />

                <input
                  type="date"
                  className="holiday-date-picker"
                  min={todayDate}
                  max="2099-12-31"
                  value={form.holiday_date}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      holiday_date: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                Holiday Type <span>*</span>
              </label>

              <select
                value={form.holiday_type}
                onChange={(e) =>
                  setForm({
                    ...form,
                    holiday_type: e.target.value,
                  })
                }
              >
                <option value="">Select Type</option>

                <option value="National">National</option>

                <option value="Festival">Festival</option>

                <option value="Company">Company</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="holiday-locations">
                Location <span>*</span>
              </label>

              <div
                className="holiday-location-picker"
                ref={locationDropdownRef}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) {
                    setShowLocationDropdown(false);
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setShowLocationDropdown(false);
                    locationDropdownRef.current?.querySelector(".holiday-location-toggle")?.focus();
                  }
                }}
              >
                <div className="holiday-location-control">
                  {selectedLocations.map((location) => (
                    <span className="holiday-location-tag" key={location.id}>
                      <span>{location.location_name}</span>
                      <button
                        type="button"
                        aria-label={`Remove ${location.location_name}`}
                        onClick={() => toggleLocation(location.id)}
                      >
                        <FiX aria-hidden="true" />
                      </button>
                    </span>
                  ))}
                  <button
                    type="button"
                    id="holiday-locations"
                    className="holiday-location-toggle"
                    aria-label="Select locations"
                    aria-expanded={showLocationDropdown}
                    aria-controls="holiday-location-options"
                    onClick={() => setShowLocationDropdown((open) => !open)}
                  >
                    {!selectedLocations.length && <span>Select Location</span>}
                    <FiChevronDown aria-hidden="true" />
                  </button>
                </div>
                {showLocationDropdown && (
                  <div id="holiday-location-options" className="holiday-location-options" role="group" aria-label="Locations">
                    {locationOptions.length > 0 && (
                      <label className="holiday-location-select-all">
                        <input
                          type="checkbox"
                          checked={allLocationsSelected}
                          ref={(input) => {
                            if (input) input.indeterminate = someLocationsSelected && !allLocationsSelected;
                          }}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setForm((previous) => ({
                              ...previous,
                              location: checked ? allLocationIds : [],
                            }));
                          }}
                        />
                        <span>Select all</span>
                      </label>
                    )}
                    {locationOptions.length ? locationOptions.map((location) => (
                      <button
                        type="button"
                        className="holiday-location-option"
                        key={location.id}
                        aria-pressed={form.location.includes(String(location.id))}
                        onClick={() => toggleLocation(String(location.id))}
                      >
                        <span>{location.location_name}</span>
                      </button>
                    )) : <p className="holiday-location-empty">No locations found</p>}
                  </div>
                )}
              </div>
            </div>

            {editId && (
              <div className="form-group">
                <label>Status</label>

                <select
                  value={form.status ? "Active" : "Inactive"}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      status: e.target.value === "Active",
                    })
                  }
                >
                  <option value="Active">Active</option>

                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Description</label>

              <textarea
                rows="4"
                value={form.description}
                onChange={(e) =>
                  setForm({
                    ...form,
                    description: e.target.value,
                  })
                }
                placeholder="Enter Description"
              />
            </div>
          </div>

          <div className="form-actions">
            <button className="cancel-btn" onClick={handleCancel}>
              Cancel
            </button>

            <button className="save-btn" onClick={handleSave}>
              {editId ? "Update" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
